import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/HrTrainingNeedsRecordLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const isMeaningful = (value) => {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim().toLowerCase();
  return !["", "-", "n/a", "na", "null", "undefined"].includes(normalized);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const toTitleFromCode = (value = "") =>
  String(value)
    .split("_")
    .filter(Boolean)
    .map(
      (token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
    )
    .join(" ");

const toSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sanitizeReportTitle = (value = "") => {
  const title = String(value || "").trim();
  if (!title) return "";
  const compact = title.replace(/\s+/g, "");
  if (compact.length <= 4 && !title.includes(" ")) {
    return "";
  }
  return title;
};

const resolveReportTitle = (headDetails = {}) => {
  const directTitle = sanitizeReportTitle(
    pickValue(headDetails, [
      "report_title",
      "form_title",
      "title",
      "transaction_name",
      "tran_name",
    ]),
  );

  if (directTitle) {
    return directTitle;
  }

  const codeTitle = sanitizeReportTitle(
    toTitleFromCode(
      pickValue(headDetails, ["transaction_code", "tran_code"]),
    ) || toTitleFromCode(pickValue(headDetails, ["transactionCode"])),
  );

  if (codeTitle) {
    return codeTitle;
  }

  return "TRAINING NEEDS RECORD";
};

const resolveRows = (itemDetails = [], headDetails = {}) => {
  const rows = (Array.isArray(itemDetails) ? itemDetails : [])
    .map((item, index) => ({
      sl_no: pickValue(item, ["sl_no", "slno", "serial_no"], index + 1),
      employee_code: pickValue(item, ["employee_code", "emp_code", "code"]),
      employee_name: pickValue(item, [
        "name_of_emp",
        "name_of_employee",
        "employee_name",
        "emp_name",
        "employee",
        "name",
      ]),
      department: pickValue(item, ["department", "dept", "name_of_department"]),
      designation: pickValue(item, ["designation", "role", "position"]),
      skill_category: pickValue(item, [
        "skill_category",
        "skill",
        "skill_type",
      ]),
      training_area_topic: pickValue(item, [
        "training_area_topic",
        "training_topic",
        "training_area",
        "topic",
      ]),
      type_of_training: pickValue(item, [
        "type_of_training",
        "training_type",
        "type",
      ]),
      training_need_identified_by: pickValue(item, [
        "training_need_identified_by",
        "identified_by",
        "need_identified_by",
      ]),
      reason_for_training_need: pickValue(item, [
        "reason_for_training_need",
        "training_need_reason",
        "reason",
      ]),
      training_priority: pickValue(item, ["training_priority", "priority"]),
      planned_month: pickValue(item, ["planned_month", "month", "plan_month"]),
      trainer_agency: pickValue(item, [
        "trainer_agency",
        "trainer_or_agency",
        "trainer",
        "agency",
      ]),
      effectiveness_evaluation: pickValue(item, [
        "effectiveness_evaluation",
        "evaluation",
        "effectiveness",
      ]),
      remarks: pickValue(item, ["remarks", "remark", "comments"]),
      hr_verified_by: pickValue(item, ["hr_verified_by", "verified_by_hr"]),
      dept_head_verified_by: pickValue(item, [
        "dept_head_verified_by",
        "department_head_verified_by",
        "verified_by_dept_head",
      ]),
    }))
    .filter((row) =>
      Object.entries(row).some(
        ([key, value]) => key !== "sl_no" && isMeaningful(value),
      ),
    );

  if (rows.length > 0) {
    return rows;
  }

  const headRow = {
    sl_no: 1,
    employee_code: pickValue(headDetails, [
      "employee_code",
      "emp_code",
      "code",
    ]),
    employee_name: pickValue(headDetails, [
      "name_of_emp",
      "name_of_employee",
      "employee_name",
      "emp_name",
      "employee",
      "name",
    ]),
    department: pickValue(headDetails, [
      "department",
      "dept",
      "name_of_department",
    ]),
    designation: pickValue(headDetails, ["designation", "role", "position"]),
    skill_category: pickValue(headDetails, [
      "skill_category",
      "skill",
      "skill_type",
    ]),
    training_area_topic: pickValue(headDetails, [
      "training_area_topic",
      "training_topic",
      "training_area",
      "topic",
    ]),
    type_of_training: pickValue(headDetails, [
      "type_of_training",
      "training_type",
      "type",
    ]),
    training_need_identified_by: pickValue(headDetails, [
      "training_need_identified_by",
      "identified_by",
      "need_identified_by",
    ]),
    reason_for_training_need: pickValue(headDetails, [
      "reason_for_training_need",
      "training_need_reason",
      "reason",
    ]),
    training_priority: pickValue(headDetails, [
      "training_priority",
      "priority",
    ]),
    planned_month: pickValue(headDetails, [
      "planned_month",
      "month",
      "plan_month",
    ]),
    trainer_agency: pickValue(headDetails, [
      "trainer_agency",
      "trainer_or_agency",
      "trainer",
      "agency",
    ]),
    effectiveness_evaluation: pickValue(headDetails, [
      "effectiveness_evaluation",
      "evaluation",
      "effectiveness",
    ]),
    remarks: pickValue(headDetails, ["remarks", "remark", "comments"]),
    hr_verified_by: pickValue(headDetails, [
      "hr_verified_by",
      "verified_by_hr",
    ]),
    dept_head_verified_by: pickValue(headDetails, [
      "dept_head_verified_by",
      "department_head_verified_by",
      "verified_by_dept_head",
    ]),
  };

  return Object.entries(headRow).some(
    ([key, value]) => key !== "sl_no" && isMeaningful(value),
  )
    ? [headRow]
    : [];
};

export const HrTrainingNeedsRecordLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
}) => {
  let organisationData = {};
  try {
    organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organisationData = {};
  }

  const refNo =
    pickValue(headDetails, ["ref_no", "reference_no", "form_ref_no"]) ||
    "FP/F/TRG/04";
  const revNo = pickValue(headDetails, [
    "rev_no",
    "revision_no",
    "form_rev_no",
  ]);
  const revDate = formatDate(
    pickValue(headDetails, ["rev_date", "revision_date", "form_rev_date"]),
  );

  const verifiedBy = pickValue(headDetails, [
    "verified_by",
    "verified_by_name",
  ]);
  const approvedBy = pickValue(headDetails, [
    "approved_by",
    "approved_by_name",
  ]);
  const reportTitle = resolveReportTitle(headDetails);
  const reportFileName =
    pickValue(headDetails, [
      "download_file_name",
      "report_file_name",
      "file_name",
    ]) ||
    toSlug(reportTitle) ||
    "manual-report";

  const rows = resolveRows(itemDetails, headDetails);

  const columnWidths = [
    "5.5%",
    "6.5%",
    "6%",
    "6%",
    "6%",
    "7%",
    "6%",
    "8%",
    "8%",
    "6%",
    "6%",
    "6%",
    "7%",
    "6%",
    "5%",
    "5%",
  ];

  const tableData = {
    headings: [
      {
        display_name: "EMPLOYEE CODE",
        field_name: "employee_code",
        is_internal: false,
      },
      {
        display_name: "EMPLOYEE NAME",
        field_name: "employee_name",
        is_internal: false,
      },
      {
        display_name: "DEPARTMENT",
        field_name: "department",
        is_internal: false,
      },
      {
        display_name: "DESIGNATION",
        field_name: "designation",
        is_internal: false,
      },
      {
        display_name: "SKILL CATEGORY",
        field_name: "skill_category",
        is_internal: false,
      },
      {
        display_name: "TRAINING AREA / TOPIC",
        field_name: "training_area_topic",
        is_internal: false,
      },
      {
        display_name: "TYPE OF TRAINING",
        field_name: "type_of_training",
        is_internal: false,
      },
      {
        display_name: "TRAINING NEED IDENTIFIED BY",
        field_name: "training_need_identified_by",
        is_internal: false,
      },
      {
        display_name: "REASON FOR TRAINING NEED",
        field_name: "reason_for_training_need",
        is_internal: false,
      },
      {
        display_name: "TRAINING PRIORITY",
        field_name: "training_priority",
        is_internal: false,
      },
      {
        display_name: "PLANNED MONTH",
        field_name: "planned_month",
        is_internal: false,
      },
      {
        display_name: "TRAINER / AGENCY",
        field_name: "trainer_agency",
        is_internal: false,
      },
      {
        display_name: "EFFECTIVENESS EVALUATION",
        field_name: "effectiveness_evaluation",
        is_internal: false,
      },
      { display_name: "REMARKS", field_name: "remarks", is_internal: false },
      {
        display_name: "HR VERIFIED BY",
        field_name: "hr_verified_by",
        is_internal: false,
      },
      {
        display_name: "DEPT HEAD VERIFIED BY",
        field_name: "dept_head_verified_by",
        is_internal: false,
      },
    ],
    rows,
  };

  return (
    <div className="hr-training-needs-page">
      <div className="hr-training-needs-card">
        <div className="hr-training-needs-header-row">
          <div className="hr-training-needs-brand">
            <img
              src={organisationData?.logo_url || ProfilePlaceholder}
              alt="Company Logo"
              className="hr-training-needs-logo"
            />
            <div className="hr-training-needs-org-name">
              {organisationData?.name || companyData?.company_name || ""}
            </div>
          </div>
          <div className="hr-training-needs-title-box">{reportTitle}</div>
          <div className="hr-training-needs-ref-box">
            <div>Ref. No. : {refNo || "-"}</div>
            <div>Rev. No. : {revNo || "-"}</div>
            <div>Rev. Date : {revDate || "-"}</div>
          </div>
        </div>

        <table className="hr-training-needs-table">
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={`training-needs-col-${index}`} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {tableData.headings.map((heading) => (
                <th key={heading.field_name}>{heading.display_name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`training-needs-row-${row.sl_no}`}>
                <td>{row.employee_code}</td>
                <td>{row.employee_name}</td>
                <td>{row.department}</td>
                <td>{row.designation}</td>
                <td>{row.skill_category}</td>
                <td>{row.training_area_topic}</td>
                <td>{row.type_of_training}</td>
                <td>{row.training_need_identified_by}</td>
                <td>{row.reason_for_training_need}</td>
                <td>{row.training_priority}</td>
                <td>{row.planned_month}</td>
                <td>{row.trainer_agency}</td>
                <td>{row.effectiveness_evaluation}</td>
                <td>{row.remarks}</td>
                <td>{row.hr_verified_by}</td>
                <td>{row.dept_head_verified_by}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={8}>Verified By: {verifiedBy}</td>
              <td colSpan={8}>Approved By: {approvedBy}</td>
            </tr>
          </tfoot>
        </table>

        <p className="invoice-system-generated-note">
          This is a system-generated document and does not require a signature.
        </p>

        <div className="invoice-buttons hr-training-needs-actions">
          <DownloadButton
            tableData={tableData}
            fileName={reportFileName}
            downloadFormats={["pdf", "full-page-pdf"]}
            fullPageDownload={true}
            fullPageSelector=".hr-training-needs-page"
          />
        </div>
      </div>
    </div>
  );
};
